// src/lib/geste-executer.js — LE CLIC DE DIMITRI, PAS CELUI DE L'OPÉRATEUR.
//
// ══ L'INVARIANT QUE CE FICHIER SERT ═════════════════════════════════════════
//
// L'opérateur ne détient JAMAIS de moyen d'agir. Il a composé une INTENTION
// (nom de levier + arguments) ; c'est CE FICHIER, dans le navigateur de
// Dimitri, avec SON jeton de session, qui appelle l'action existante.
// L'acteur audité est donc Dimitri.
//
// ⚠️ C'est pour cela que l'exécution vit CÔTÉ CLIENT et nulle part ailleurs.
// La tentation naturelle serait que `operateur_proposition_confirm` appelle le
// levier côté serveur : l'acteur resterait techniquement Dimitri, mais le
// chemin existerait — et le jour où quelqu'un veut « accepter
// automatiquement », il est déjà tracé. Une sentinelle interdit à tout
// `lib/operateur-*.js` d'importer un module de levier ; ce fichier-ci est
// l'autre moitié de la garde.
//
// ── LE DESSIN EST CELUI QUI EXISTE DÉJÀ POUR LES MESSAGES ───────────────────
// `src/views/operateur/OperateurView.jsx` fait déjà, pour un brouillon :
//   réclamer → appeler le guichet existant → confirmer.
// Un geste suit la MÊME séquence, avec un autre guichet. On ne réinvente pas
// une machinerie parce que l'objet change.

import { supabase } from "../supabase";
// ⚠️ Le MIROIR ESM, pas le CJS : create-react-app refuse tout import hors de
// `src/`. La parité entre les deux est de SÉCURITÉ, pas de confort — un
// registre client qui divergerait autoriserait un levier que le serveur
// interdit. Elle est verrouillée par src/lib/operateur-leviers-parite.test.js.
import {
  lireLevier,
  filtrerArgs,
  manqueIndispensable,
  poserChemin,
  saisieSApplique,
} from "./operateur-leviers";

// La borne d'un TEXTE relu sur la carte — la MÊME que celle du socle
// (`lib/leviers/_forme.js::MAX_CARS_TEXTE`, 4 000). Recopiée ici parce que le
// miroir `src/lib/leviers/_forme.js` est un doublon CJS de parité qui requiert
// `app-carte.js`, hors de portée de webpack ; l'égalité est verrouillée par
// `src/lib/geste-executer-textes.test.js`.
export const MAX_CARS_TEXTE = 4000;

// ══ LES GUICHETS OUVERTS AUX GESTES — LA LISTE BLANCHE, CÔTÉ NAVIGATEUR ═════
//
// La MÊME liste que `lib/leviers/_forme.js::ENDPOINTS_AUTORISES` (égalité
// verrouillée par `src/lib/leviers-endpoints-autorises.test.js`). Elle existe
// ici parce que le miroir client du registre N'EST PAS revalidé au
// chargement : côté serveur, un levier hors liste fait rougir la conformité ;
// côté navigateur, rien ne le relit — sauf cette ligne, au clic. Fermée, pas
// un motif : `/^\/api\//` ouvrirait Stripe et les envois par la même ligne.
//
// ⚠️ `/api/stock` (2026-09-16, lot 5) est le premier guichet hors du routeur.
// Sa réponse n'a pas la forme du routeur : un 200 rend l'instantané du stock
// (pas de `ok`, pas d'`audit_id`), un refus rend `{ error }` avec 400 / 403 /
// 409, jamais `verdict` ni `queued`. Voir `_lireVerdict`.
export const ENDPOINTS_AUTORISES = Object.freeze([
  "/api/beds24-mapping",
  "/api/stock",
]);

/**
 * Ce que l'appelant garde d'une réponse 200, selon le guichet.
 *
 * ⚠️ `/api/stock` rend l'instantané COMPLET (dépôts avec e-mails d'agents,
 * achats avec prix) : on n'en garde rien. La carte n'a besoin que d'une
 * référence pour la trace, et le stock n'en rend pas — `resultat` dit juste
 * que c'est fait. Le routeur, lui, rend `{ ok, audit_id | id, … }` : inchangé.
 */
function _resultatDe(endpoint, json) {
  if (endpoint === "/api/stock") return { ok: true, endpoint };
  return json;
}

/** Ajoute le point final s'il manque — sans en doubler un déjà là. */
function _finirPhrase(t) {
  const p = String(t || "").trim();
  return /[.!?…]$/.test(p) ? p : `${p}.`;
}

async function jetonSession() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token)
    throw new Error("Session expirée — reconnectez-vous.");
  return session.access_token;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Un identifiant pour un geste DEMANDÉ (qui n'a pas de ligne en base). */
function _idDeGeste() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID)
      return crypto.randomUUID();
  } catch {
    /* pas de crypto : on tombe sur l'aléa simple */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * NOTE SON PARI — juste après que le levier a répondu OK.
 *
 * Dimitri, 2026-09-13 : « il propose, je valide, IL NOTE SON PARI, il revient
 * dire ce que ça a donné. » Le pari se pose AU MOMENT DU GESTE (ici), jamais
 * de la proposition : une carte non cliquée n'engage rien. Le serveur le
 * compose (lib/operateur-pari.js) et l'écrit dans `operateur_actes.meta`.
 *
 * ⚠️ NE PEUT JAMAIS FAIRE ÉCHOUER LE GESTE. Le geste EST parti ; au pire on
 * perd une mesure. L'inverse — un geste annoncé raté parce que sa trace n'a
 * pas pu s'écrire — serait absurde. Même doctrine que l'empreinte du brief
 * (`handleOperateurBrief`). Donc : jamais de `throw`, jamais de retour qui
 * change celui d'`executerGeste`. Mais l'appel est AWAITÉ (pas de
 * fire-and-forget) : on sait au moins qu'il a été tenté.
 *
 * @returns {Promise<{note:boolean, pari:object|null}>}
 */
export async function noterLePari({
  token,
  levier,
  args,
  proposition,
  reference,
}) {
  try {
    const propId =
      typeof proposition?.id === "string" && UUID_RE.test(proposition.id)
        ? proposition.id
        : null;
    const res = await fetch("/api/beds24-mapping", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        action: "operateur_pari_noter",
        levier,
        args,
        proposition_id: propId,
        geste_id: propId ? null : _idDeGeste(),
        reference: reference || null,
      }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.ok !== true) {
      console.error(
        "[geste] le pari n'a pas pu être noté — le geste est parti",
        {
          statut: res.status,
          error: json.error || null,
        },
      );
      return { note: false, pari: null };
    }
    return { note: true, pari: json.pari || null };
  } catch (e) {
    console.error(
      "[geste] le pari n'a pas pu être noté — le geste est parti",
      e,
    );
    return { note: false, pari: null };
  }
}

/**
 * Le verdict d'ouverture porté par un geste : `true`, `false`, ou `null` s'il
 * ne le dit pas. Exporté pour la carte, qui grise le bouton AVANT le clic.
 *
 * ⚠️ `=== true` STRICT, jamais « truthy » ni `!== false`. Ce marqueur remplace
 * une liste de numéros de vague que le navigateur repassait dans
 * `leviersOuverts` : cette liste PERDAIT l'ouverture levier par levier (la
 * forme réellement en prod), et faisait vivre ici une SECONDE règle, qui
 * diverge au premier correctif appliqué d'un seul côté. Le serveur tranche une
 * fois ; le navigateur lit. Tout ce qui n'est pas un `true` franc grise.
 */
export function gesteOuvertDe(geste) {
  const brut = geste && geste.geste_ouvert;
  if (brut === true) return true;
  if (brut === false) return false;
  // Ni ouvert ni fermé : la carte ne le dit pas (vieil onglet, geste neuf).
  return null;
}

/** `null` si le geste est ouvert ; sinon le refus, prêt à rendre. */
export function _refusDeVague(prop, levier) {
  const ouvert = gesteOuvertDe(prop);
  if (ouvert === null)
    return {
      ok: false,
      etape: "vague",
      // ⚠️ PLUS UN MOT DE « VAGUE » ICI (2026-09-28). Le navigateur ne les
      // connaît plus — il lit un verdict — et la règle du refus serveur vaut
      // aussi pour celui-ci : le numéro reste dans le champ machine, jamais
      // dans la phrase. Dimitri n'a rien à chercher : il a un écran vieux d'un
      // déploiement, et un seul geste à faire.
      message:
        "Cet écran date d'avant une mise à jour — rechargez-le. Rien n'a été fait.",
    };
  if (ouvert !== true)
    return {
      ok: false,
      etape: "vague",
      vague: levier.vague,
      // ⚠️ SANS NUMÉRO DE VAGUE, comme le refus serveur (`_vagueFermee`,
      // lib/operateur-handlers.js) : les gestes s'ouvrent un par un, citer la
      // vague ferait chercher un ensemble fermé qui ne l'est pas. Le numéro
      // reste dans `vague`, juste au-dessus.
      message:
        "Ce geste n'est pas encore ouvert — il ne peut pas partir d'ici. Rien n'a été fait.",
    };
  return null;
}

/**
 * Exécute un geste proposé.
 *
 * @param {object} p
 * @param {object} p.proposition — la carte ({id, levier, args, motif, choix, textes})
 *        `choix` = ce que Dimitri a CHOISI sur la carte (les clés
 *        `args_choisis` du levier), jamais composé par le modèle.
 *        `textes` = ce que Dimitri a RELU ET CORRIGÉ sur la carte (les clés
 *        `args_textes` du levier) — c'est CE texte qui part, jamais l'original.
 * @param {(a:object)=>Promise<object>} [p.postAction] — l'appel aux actions
 *        opérateur (réclamer / confirmer), fourni par l'écran.
 *
 * ⚠️ `postAction` EST FACULTATIF DEPUIS LE 2026-09-09, et c'est le seul écart.
 * Un geste né d'une PROPOSITION vit en base : il se réclame et se confirme,
 * pour qu'un second onglet ne le refasse pas. Un geste que Dimitri vient de
 * DEMANDER dans la conversation n'existe nulle part — il n'y a rien à
 * réclamer, et confirmer une ligne qui n'existe pas échouerait en annonçant
 * « le geste est parti mais sa trace a manqué », ce qui serait faux et
 * inquiétant. Sans `postAction`, on garde donc tout le reste : la relecture du
 * registre, le filtrage des arguments, le jeton de Dimitri, et surtout les
 * trois natures d'échec — dont « on ne sait pas si c'est parti ».
 * @returns {Promise<{ok:true, resultat:object} | {ok:false, etape:string, message:string}>}
 *
 * Ne LANCE jamais : un geste qui échoue est une information, pas un plantage.
 * `etape` dit OÙ ça s'est arrêté — c'est ce qui permet de savoir si quelque
 * chose est parti ou non, et c'est la seule question qui compte après un échec.
 */
/**
 * Une saisie de la carte, validée selon sa nature — ou `null`.
 *
 * `montant` : un nombre fini strictement positif, virgule acceptée (« 17,24 »),
 * arrondi au centime.
 * `montant_signe` (lot 6) : le signe compte — « -20 » est une remise, « 20 »
 * un supplément ; zéro n'est ni l'un ni l'autre, refusé.
 * `nombre` (lot 6) : un nombre ≥ 0 sans euro (un taux, en %), deux décimales.
 * Une nature inconnue rend `null` : bouton gris, jamais une valeur devinée —
 * `_forme.js::NATURES_SAISIES` et cette liste sont tenues ensemble par test.
 */
export const NATURES_SAISIES = Object.freeze([
  "montant",
  "montant_signe",
  "nombre",
]);
export function valeurSaisie(brut, nature) {
  if (!NATURES_SAISIES.includes(nature)) return null;
  const t = String(brut == null ? "" : brut)
    .trim()
    .replace(",", ".");
  const re =
    nature === "montant_signe" ? /^-?\d+(?:\.\d+)?$/ : /^\d+(?:\.\d+)?$/;
  if (!re.test(t)) return null;
  const n = Math.round(Number(t) * 100) / 100;
  if (!Number.isFinite(n) || Math.abs(n) > 1000000) return null;
  if (nature === "montant") return n > 0 ? n : null;
  if (nature === "montant_signe") return n !== 0 ? n : null;
  return n >= 0 ? n : null;
}

export async function executerGeste({ proposition, postAction }) {
  const prop = proposition || {};

  // ── 0 · RE-VALIDER LE LEVIER, ICI ET MAINTENANT ─────────────────────────
  // La carte porte `appel` recopié du registre au moment où elle est née.
  // ⚠️ Une carte vieille de deux jours NE FAIT PAS FOI : un levier a pu être
  // retiré de l'allow-list entre-temps, précisément parce qu'on l'a jugé
  // dangereux. On relit donc le registre, jamais la carte.
  const levier = lireLevier(prop.levier);
  if (!levier) {
    return {
      ok: false,
      etape: "levier",
      message:
        "Ce geste n'est plus proposable (le levier a été retiré). Rien n'a été fait.",
    };
  }
  // ── 0-guichet · L'ENDPOINT EST-IL OUVERT AUX GESTES ? (2026-09-16) ────────
  // Le registre client n'est pas revalidé au chargement : c'est ici, et
  // seulement ici, qu'un levier visant un guichet hors liste est arrêté avant
  // tout appel. FAIL-CLOSED — un endpoint absent ou inconnu ne part pas.
  if (!ENDPOINTS_AUTORISES.includes(levier.endpoint)) {
    return {
      ok: false,
      etape: "endpoint",
      message:
        "Ce geste vise un guichet qui n'est pas ouvert aux gestes. Rien n'a été fait.",
    };
  }

  // ── 0-vague · SA VAGUE EST-ELLE OUVERTE ? (audit du 2026-09-13) ───────────
  // Les vagues ne filtraient que le catalogue offert au modèle : un geste
  // porté par un événement arrivait ici sans jamais y passer — dont deux
  // leviers de vague 4, chemin argent. Dimitri : « prépare-la, mais ne
  // l'ouvre pas encore. »
  //
  // Le navigateur ne lit pas l'environnement du serveur : le VERDICT arrive
  // marqué sur le geste par celui qui l'a servi (événements, liste,
  // conversation), qui l'a pris chez `leviersOuverts` — levier par levier.
  // Ici, on le LIT : rejuger sur une liste de numéros perdait cette finesse.
  // ⚠️ FAIL-CLOSED : un geste qui ne dit pas qu'il est ouvert ne part pas. Le
  // serveur le refuse de toute façon (événements non servis, réclamation
  // refusée) — ceci évite un clic qui promet et échoue.
  const vaguesRefus = _refusDeVague(prop, levier);
  if (vaguesRefus) return vaguesRefus;

  // ══ DETTE CONNUE — LE VERDICT EST FIGÉ À L'INSTANT DU SERVICE ══════════════
  //
  // Ce qui précède LIT un verdict rendu quand la carte a été servie. Rien ne le
  // re-juge à l'EXÉCUTION :
  //   · une carte de la file passe par `operateur_proposition_claim`, qui
  //     revérifie serveur (`_vagueFermee`) — celle-là est couverte ;
  //   · une carte SANS ligne en base (conversation, événement, feuille) saute
  //     la réclamation (voir la garde `prop.id` plus bas) et frappe
  //     directement `levier.endpoint` — et AUCUN endpoint métier ne consulte
  //     `leviersOuverts`.
  //
  // ⚠️ CONSÉQUENCE, CHEMIN ARGENT : un onglet laissé ouvert, un déploiement qui
  // referme `pricing_set_manual`, et la carte reste cliquable — le prix part
  // RÉELLEMENT. Ce n'est pas une régression (la liste de numéros d'avant gelait
  // exactement pareil), mais ce lot a déplacé TOUTE la garde vers l'instant du
  // service : le trou est désormais le seul qui reste.
  //
  // ⚠️ ET `compose_at` NE LE BOUCHE PAS. Il est posé pour ça — le lot suivant
  // doit refuser un clic différé côté SERVEUR, avec un motif que Dimitri lit
  // (« ce geste a été proposé il y a 6 heures ») — mais tant que cette garde
  // n'existe pas, l'heure n'est qu'une donnée transportée. Ne pas lire ce
  // champ comme « le sujet est traité ».

  const args = filtrerArgs(prop.levier, prop.args || {});
  if (args === null) {
    // Un argument hors de la liste fermée : on refuse l'intention ENTIÈRE.
    // L'ignorer laisserait partir un geste à moitié compris.
    return {
      ok: false,
      etape: "arguments",
      message:
        "Ce geste porte un argument qui n'est pas autorisé. Rien n'a été fait.",
    };
  }

  // ── 0-indispensable · CE QUE LE SERVEUR EXIGE ET QUE LE GESTE N'A PAS ────
  // (revue adverse #1440) `pricing_set_manual` sans `price_eur` répond 400 :
  // la carte le grise déjà, mais un « oui » tapé ou une carte servie par une
  // version antérieure passerait par ici. Même juge que la carte et que le
  // serveur (`manqueIndispensable`, registre miroir), jamais une règle à côté.
  const manque = manqueIndispensable(prop.levier, args);
  if (manque)
    return {
      ok: false,
      etape: "arguments",
      message: `${_finirPhrase(manque.phrase)} Ce geste ne peut pas partir. Rien n'a été fait.`,
    };

  // ── 0bis · CE QUE DIMITRI A CHOISI SUR LA CARTE (2026-09-13) ─────────────
  // Un argument `args_choisis` n'est pas dans `args` (le modèle ne le compose
  // pas) : il arrive à part, et il est validé à part — une clé qui n'est pas
  // déclarée choisie est refusée, une clé déclarée mais non choisie bloque
  // le geste. On ne devine jamais un agent.
  const choisis = levier.args_choisis || {};
  const choix = prop.choix && typeof prop.choix === "object" ? prop.choix : {};
  for (const cle of Object.keys(choix)) {
    if (!Object.prototype.hasOwnProperty.call(choisis, cle))
      return {
        ok: false,
        etape: "arguments",
        message:
          "Ce geste porte un argument qui n'est pas autorisé. Rien n'a été fait.",
      };
  }
  for (const cle of Object.keys(choisis)) {
    const v = choix[cle];
    if (typeof v !== "string" || !v.trim() || v.length > 120)
      return {
        ok: false,
        etape: "choix",
        message: `Choisissez « ${choisis[cle].etiquette || cle} » sur la carte avant de lancer ce geste. Rien n'a été fait.`,
      };
  }

  // ── 0ter · CE QUE DIMITRI A RELU ET CORRIGÉ (2026-09-13) ─────────────────
  // Dimitri : « tu as raison de refuser qu'un texte tout fait parte d'un clic.
  // Avec le champ, je lis, je corrige si besoin, j'envoie. »
  //
  // Un argument `args_textes` (le corps d'une réponse au voyageur) arrive
  // composé dans `args`, et Dimitri peut le réécrire sur la carte : la version
  // corrigée REMPLACE l'originale, et repasse par les mêmes bornes que le texte
  // composé (clé dans la liste fermée, longueur ≤ MAX_CARS_TEXTE).
  //
  // ⚠️ FAIL-CLOSED SUR LE VIDE. Un champ effacé ne « retombe » PAS sur le texte
  // proposé — ce serait faire partir précisément ce que Dimitri vient de
  // refuser. Et il ne part pas vide non plus : un corps vide chez un voyageur
  // serait pire qu'un refus. Le geste s'arrête, et le dit.
  const clesTextes = Array.isArray(levier.args_textes)
    ? levier.args_textes
    : [];
  const textes =
    prop.textes && typeof prop.textes === "object" ? prop.textes : {};
  for (const cle of Object.keys(textes)) {
    // Une réécriture sur une clé qui n'est pas un texte déclaré ouvrirait les
    // CIBLES (identifiants, dates, montants) à l'édition — refus entier.
    if (!clesTextes.includes(cle))
      return {
        ok: false,
        etape: "arguments",
        message:
          "Ce geste porte un argument qui n'est pas autorisé. Rien n'a été fait.",
      };
  }
  for (const cle of clesTextes) {
    const corrige = Object.prototype.hasOwnProperty.call(textes, cle);
    if (!corrige && !Object.prototype.hasOwnProperty.call(args, cle)) continue;
    const v = corrige ? textes[cle] : args[cle];
    if (typeof v !== "string" || !v.trim())
      return {
        ok: false,
        etape: "texte",
        message: `Le texte « ${cle} » est vide — relisez-le sur la carte avant de lancer ce geste. Rien n'a été fait.`,
      };
    if (v.length > MAX_CARS_TEXTE)
      return {
        ok: false,
        etape: "texte",
        message: `Le texte « ${cle} » dépasse ${MAX_CARS_TEXTE} caractères. Rien n'a été fait.`,
      };
    if (corrige) args[cle] = v;
  }

  // ── 0ter-bis · CE QUE DIMITRI A TAPÉ SUR LA CARTE — `args_saisis` ────────
  // (revue #1501, M4). Le prix payé d'un achat n'est pas un argument que le
  // modèle écrit : c'est un montant que Dimitri TAPE, comme un motif. Il
  // arrive à part (`prop.saisies`), est validé à part (un nombre fini > 0
  // pour un montant), et se pose AU CHEMIN déclaré — y compris dans un objet
  // composé (`achat.prixTotal`). Ce qui est tapé ÉCRASE ce que `args`
  // porterait sous ce chemin : le serveur l'a déjà retiré, une carte servie
  // avant cette règle pourrait encore l'avoir. FAIL-CLOSED : une saisie
  // déclarée et vide ne part pas — le serveur répondrait 400 sur un bouton
  // qui promettait.
  const saisis =
    levier.args_saisis && typeof levier.args_saisis === "object"
      ? levier.args_saisis
      : {};
  const saisies =
    prop.saisies && typeof prop.saisies === "object" ? prop.saisies : {};
  for (const chemin of Object.keys(saisies))
    if (!Object.prototype.hasOwnProperty.call(saisis, chemin))
      return {
        ok: false,
        etape: "arguments",
        message:
          "Ce geste porte un argument qui n'est pas autorisé. Rien n'a été fait.",
      };
  let argsAvecSaisies = args;
  for (const [chemin, decl] of Object.entries(saisis)) {
    // Une saisie conditionnelle hors de sa condition (lot 6) : l'argument est
    // composé, pas tapé — rien à poser, et rien à exiger ici (c'est
    // `manqueIndispensable` qui l'a réclamé s'il manquait).
    if (!saisieSApplique(decl, args)) continue;
    const v = valeurSaisie(saisies[chemin], decl && decl.nature);
    if (v === null)
      return {
        ok: false,
        etape: "saisie",
        message: `Tapez ${(decl && decl.libelle) || "la valeur demandée"} sur la carte avant de lancer ce geste. Rien n'a été fait.`,
      };
    argsAvecSaisies = poserChemin(argsAvecSaisies, chemin, v);
  }
  Object.assign(args, argsAvecSaisies);

  // ── 0quater · CE QUE LA CARTE A LU AVANT LE CLIC (2026-09-14) ────────────
  // Un levier qui déclare un `apercu` a fait lire à la carte la valeur
  // ACTUELLE — et un jeton de version. Le geste ne part pas sans ce jeton :
  // il voyage dans l'en-tête que le levier nomme. FAIL-CLOSED : une carte qui
  // n'a pas lu ne fait pas partir un geste qui promet « X → Y ».
  //
  // ⚠️ CE QUE LE JETON PROUVE, ET CE QU'IL NE PROUVE PAS (revue adverse
  // #1440, correction 8). Il prouve que la carte A LU l'état avant le clic.
  // Il ne prouve PAS que rien n'a bougé depuis : seules les écritures qui
  // appellent `verifierJetonApercu` (lib/apercu-jeton.js) le comparent à
  // l'état frais et refusent (409) s'il a changé — au 2026-09-16, ce sont
  // `site_page_publication` (lib/site-publication-handlers.js) et
  // `booking_decision` (lib/booking-decision-handlers.js). Les écritures de
  // prix (`pricing_set_manual`, `pricing_unlock`, `pricing_apply_base`,
  // `pricing_block_create`, prix de base / plancher / séjour minimum) ne le
  // vérifient pas — dette nommée en tête de lib/pricing-config-lire.js et
  // lib/pricing-lectures-apercu.js. Entre l'affichage et le clic, c'est la
  // garde du handler (409 block_overlap, 400 below_floor…) qui tranche.
  const enTetesApercu = {};
  if (levier.apercu && typeof levier.apercu === "object") {
    const jeton =
      prop.apercu && typeof prop.apercu.jeton === "string"
        ? prop.apercu.jeton.trim()
        : "";
    if (!jeton || jeton.length > 128)
      return {
        ok: false,
        etape: "apercu",
        message:
          "La valeur actuelle n'a pas été lue sur la carte — ce geste ne part pas sans elle. Rien n'a été fait.",
      };
    enTetesApercu[levier.apercu.jeton] = jeton;
  }

  // ── 0quinquies · LA CONFIRMATION DE DIMITRI (2026-09-17) ────────────────
  // Elle n'est PAS un argument : un argument déclaré est un argument que le
  // modèle écrit, et `confirm: true` composé par le modèle viderait la
  // première des trois gardes de la réouverture (ADR-256/257). Elle vient de
  // la carte, du même endroit que le motif tapé. Sans elle, le geste ne part
  // pas du tout — laisser partir ce que le serveur refusera par un 400
  // `confirm_required` serait un clic pour rien, sur le chemin argent.
  if (levier.confirmation_au_clic === true && prop.confirme !== true)
    return {
      ok: false,
      etape: "confirmation",
      message:
        "Ce geste demande votre confirmation sur la carte avant de partir. Rien n'a été fait.",
    };

  // ── 1 · RÉCLAMER ────────────────────────────────────────────────────────
  // Le serveur pose `en_envoi` avec un contrôle du nombre de lignes : si un
  // autre onglet a déjà réclamé, on s'arrête net, avant tout appel de levier.
  // Sauté pour un geste DEMANDÉ : il n'y a pas de ligne à réclamer.
  try {
    if (postAction)
      await postAction({ action: "operateur_proposition_claim", id: prop.id });
  } catch (e) {
    return {
      ok: false,
      etape: "reclamation",
      message:
        e?.message ||
        "Ce geste a déjà été pris en charge ailleurs. Rien n’a été fait.",
    };
  }

  // ── 2 · APPELER LE LEVIER — depuis CE navigateur, avec CE jeton ─────────
  //
  // ⚠️ LE JETON SE PREND AVANT LE TRY, ET C'EST UNE CORRECTION (2026-09-09).
  // Il était pris DEDANS : une session expirée levait, et le catch la classait
  // « on ne sait pas si c'est parti » — alors qu'on le sait parfaitement,
  // puisque la requête n'est jamais partie. Le message inquiétait pour rien ET
  // bloquait le bouton « Réessayer », qui est justement ce qu'il faut faire
  // après s'être reconnecté. Une incertitude annoncée à tort coûte plus qu'une
  // erreur franche.
  let resultat;
  let token;
  try {
    token = await jetonSession();
  } catch (e) {
    return {
      ok: false,
      etape: "session",
      message: `${e?.message || e} Rien n'a été fait.`,
    };
  }
  try {
    const corps = { action: levier.action, ...args, ...choix };
    // Le motif figé accompagne le geste quand le levier l'exige. Il vient de
    // lib/geste-motif.js — des faits vérifiés en base, jamais du modèle.
    // ⚠️ LE NOM DU MOTIF VIENT DU REGISTRE (2026-09-09). On envoyait `raison`
    // ET `motif` en espérant tomber juste — mais les handlers attendent aussi
    // `reason` et `texte`. Le motif que Dimitri écrit, sur une carte qui le
    // lui IMPOSE, partait donc dans le vide ; et là où le serveur l'exige, le
    // geste était refusé. Envoyer plusieurs noms « au cas où » n'était pas une
    // précaution : c'était une devinette qui se donnait l'air d'en être une.
    if (levier.motif_requis && levier.motif_champ && prop.motif)
      corps[levier.motif_champ] = prop.motif;
    // ⚠️ LA CONFIRMATION SE POSE ICI, ET SEULEMENT SI DIMITRI L'A COCHÉE
    // (2026-09-17). Elle n'est PAS un argument : un argument déclaré est un
    // argument que le modèle écrit, et `confirm: true` composé par le modèle
    // viderait la première des trois gardes de la réouverture (ADR-256/257).
    // Elle vient de la carte, du même endroit que le motif tapé — et sans
    // elle, le geste ne part pas du tout : laisser partir un geste qui sera
    // refusé par un 400 `confirm_required` serait un clic pour rien, sur le
    // chemin argent.
    if (levier.confirmation_au_clic === true) corps.confirm = true;
    // ⚠️ LE VERBE VIENT DU REGISTRE (2026-09-09). Il était codé en dur à POST,
    // et `livret_config_publier` pointe une action dont POST CRÉE un livret
    // quand PATCH le publie : le geste aurait rendu un 409 sur un bouton qui
    // promettait de publier. Une action à plusieurs verbes est un piège que
    // seul le registre peut désamorcer.
    const res = await fetch(levier.endpoint, {
      method: levier.methode === "PATCH" ? "PATCH" : "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...enTetesApercu,
      },
      body: JSON.stringify(corps),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      // ⚠️ C'EST LE LEVIER QUI VALIDE, PAS NOUS. Un refus ici (409, 400) veut
      // dire que l'état a changé depuis la proposition — la caution a déjà été
      // libérée, l'alerte déjà traitée. Ce n'est pas une panne : c'est la
      // garde qui fait son travail, et la carte doit devenir « périmée ».
      return {
        ok: false,
        etape: "levier_refuse",
        statut: res.status,
        // ⚠️ LE MESSAGE DU SERVEUR D'ABORD (2026-09-09). On ne lisait que
        // `error` — un code technique — alors que les handlers renvoient un
        // `message` en français qui dit POURQUOI : « prix sous le plancher »,
        // « politique encore affectée à un logement ». Sans lui, Dimitri lit
        // « refusé (400) » et doit aller deviner sur l'écran : le refus a
        // l'air d'un caprice alors que c'est une garde qui fait son travail.
        // ⚠️ ET « RIEN N'A ÉTÉ FAIT » RESTE COLLÉ AU MESSAGE. Le motif du
        // serveur explique le refus ; il ne dit pas si quelque chose est
        // parti à moitié. C'est pourtant la seule question qui compte quand
        // on vient de cliquer, et la laisser sans réponse pousse à recliquer.
        // La ponctuation du serveur est respectée : « Déjà traité ailleurs. »
        // suivi d'un point donnerait « ailleurs.. », qui a l'air d'un bug.
        // …SAUF quand le serveur sait mieux (revue adverse #1424, MAJEUR 2) :
        // sur un VERDICT Beds24, la phrase du serveur dit l'état réel — « rien
        // n'a été libéré », « le séjour minimum reste à X », ou « le blocage
        // est enregistré chez vous mais la période reste OUVERTE sur les
        // plateformes » (2026-09-15, blocage manuel : quelque chose A été
        // fait, et « Rien n'a été fait » serait faux). Coller le suffixe
        // derrière annulerait la phrase qui compte, sur exactement le cas où
        // l'ambiguïté coûte. Un verdict = le serveur a le dernier mot.
        // Revue adverse #1428 : idem quand l'envoi est EN FILE (`queued`, Beds24
        // injoignable) — quelque chose est parti ou va partir. Et seulement si
        // le serveur a écrit une PHRASE (MINEUR 1) : un code nu sans suffixe
        // serait « beds24_refused. », muet sur la seule question qui compte.
        // `/api/stock` (2026-09-16) n'écrit jamais `verdict` ni `queued` : ses
        // refus sont `{ error }` en français (« Réservé admin », « Mois clos »,
        // l'aperçu périmé) et tombent sur la branche « Rien n'a été fait » —
        // ce qui est vrai : le stock refuse AVANT d'écrire, toujours.
        message:
          (json.verdict || json.queued) && json.message
            ? _finirPhrase(json.message)
            : `${_finirPhrase(
                json.message ||
                  json.error ||
                  `Le geste a été refusé (${res.status})`,
              )} Rien n'a été fait.`,
      };
    }
    resultat = _resultatDe(levier.endpoint, json);
  } catch (e) {
    // Réseau ou session : on NE SAIT PAS si le geste est parti. Le dire.
    return {
      ok: false,
      etape: "levier_incertain",
      message: `Le geste a été tenté mais on ne sait pas s'il a abouti — vérifiez ${levier.ecran} avant de recommencer. (${e?.message || e})`,
    };
  }

  // ── 2bis · NOTER SON PARI — le geste EST parti, on écrit ce qu'on en attend.
  // Fail-soft par construction (voir `noterLePari`) : rien ici ne peut
  // changer l'issue du geste. Awaité pour ne pas être un fire-and-forget.
  // `args` porte le texte CORRIGÉ (0ter) : la trace dit ce qui est parti.
  const pari = await noterLePari({
    token,
    levier: prop.levier,
    args,
    proposition: prop,
    reference: resultat?.audit_id || resultat?.id || null,
  });

  // ── 3 · CONFIRMER ───────────────────────────────────────────────────────
  // On rend au serveur la RÉFÉRENCE de ce que le levier a fait, jamais le
  // pouvoir de le refaire.
  // Rien à confirmer pour un geste demandé : il n'a pas de ligne en base.
  if (!postAction)
    return {
      ok: true,
      resultat,
      constatable: levier.constatable,
      ecran: levier.ecran,
      pari,
    };
  try {
    await postAction({
      action: "operateur_proposition_confirm",
      id: prop.id,
      geste: {
        levier: prop.levier,
        reference: resultat?.audit_id || resultat?.id || null,
      },
    });
  } catch (e) {
    // Le geste EST parti ; seule la trace a manqué. Ne jamais dire l'inverse.
    return {
      ok: false,
      etape: "confirmation",
      message: `Le geste est parti, mais sa trace n'a pas pu être enregistrée. Ne le refaites pas. (${e?.message || e})`,
    };
  }

  return {
    ok: true,
    resultat,
    constatable: levier.constatable,
    ecran: levier.ecran,
    pari,
  };
}
